import { Box, Group, Progress, Stack, Text } from "@mantine/core"
import { WarningIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"

export type SlaSeverity = "OK" | "WARNING" | "CRITICAL"

interface Props {
  // Deadline timestamp (ISO). Falls back to a 48-hour window from createdAt.
  deadline: string | null | undefined
  createdAt?: string | null
  // When true, render just the count-down text (no progress bar, no
  // breach caption). Used by active-deliveries where the row is dense.
  compact?: boolean
}

const DEFAULT_WINDOW_HOURS = 48

export function computeSla(deadline?: string | null, createdAt?: string | null) {
  const now = dayjs()
  const start = createdAt ? dayjs(createdAt) : now
  const end = deadline ? dayjs(deadline) : start.add(DEFAULT_WINDOW_HOURS, "hour")
  const remainingMs = end.diff(now)
  const totalMs = end.diff(start)
  const pct = Math.max(0, Math.min(100, (remainingMs / Math.max(totalMs, 1)) * 100))
  const remainingHours = remainingMs / (1000 * 60 * 60)

  let severity: SlaSeverity = "OK"
  if (remainingHours < 4) severity = "CRITICAL"
  else if (remainingHours < 24) severity = "WARNING"

  return { remainingMs, pct, severity, end }
}

function formatRemaining(ms: number) {
  if (ms <= 0) return "Хугацаа өнгөрсөн"
  const totalMinutes = Math.floor(ms / (1000 * 60))
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60)
  const minutes = totalMinutes - days * 60 * 24 - hours * 60
  if (days >= 1) return hours > 0 ? `${days} хоног ${hours}ц` : `${days} хоног`
  if (hours >= 1) return minutes > 0 ? `${hours}ц ${minutes}м` : `${hours}ц`
  return `${minutes}м`
}

const SEVERITY_COLOR: Record<SlaSeverity, string> = {
  OK: "green",
  WARNING: "yellow",
  CRITICAL: "red",
}

export function SlaCell({ deadline, createdAt, compact }: Props) {
  const { remainingMs, pct, severity } = computeSla(deadline, createdAt)
  const color = SEVERITY_COLOR[severity]

  if (compact) {
    return (
      <Group gap={4} align="center" wrap="nowrap">
        {severity === "CRITICAL" && (
          <WarningIcon size={12} color={`var(--mantine-color-${color}-7)`} weight="fill" />
        )}
        <Text size="11px" fw={600} c={`${color}.7`}>
          {formatRemaining(remainingMs)}
        </Text>
      </Group>
    )
  }

  return (
    <Stack gap={2} miw={90}>
      <Group gap={4} align="center" wrap="nowrap">
        {severity === "CRITICAL" && (
          <WarningIcon size={12} color={`var(--mantine-color-${color}-7)`} weight="fill" />
        )}
        <Text size="sm" fw={600} c={color}>
          {formatRemaining(remainingMs)}
        </Text>
      </Group>
      <Progress value={pct} color={color} size="xs" radius="xl" w={80} />
      {severity === "CRITICAL" && (
        <Box>
          <Text size="9px" c="red.7" fw={500}>
            SLA хугацаа дуусахад ойртсон
          </Text>
        </Box>
      )}
    </Stack>
  )
}
